import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Eye, EyeOff } from "lucide-react";
import api, { ROLE_LABELS } from "@/lib/api";

const ROLES = ["admin", "manager", "leader", "sr_engineer", "jr_engineer", "drafter", "qa1", "qa2", "final_qc", "hr_manager", "office_assistant"];
const DESIGNATIONS = [
  "Team Manager",
  "Team Leader",
  "Sr. Engg. (CAD Technican)",
  "Jr. Engg. (CAD Technican)",
  "Trainee (CAD Technican)",
  "HRA",
  "OfficeBoy",
];
const DEPARTMENTS = [
  "Admin",
  "Land Surveying & Development",
  "Civil Engg.",
  "Architectural",
  "BIM | Steel | Mechanical",
  "IT & Technology",
];

// Admin: Create User (email + password + role + team_role + manager)
export default function CreateUserModal({ allUsers, onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "jr_engineer", team_role: "employee",
    manager_id: "", team: "", employee_id: "", monthly_salary_inr: 0,
    designation: "", department: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#";
    let pw = "";
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    upd("password", pw);
    setShowPw(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) { toast.error("Email and password are required"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Created ${form.email}`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
    setSaving(false);
  };

  return createPortal((
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-xl w-full flex flex-col min-h-0" style={{ maxHeight: "calc(100vh - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 bg-white shrink-0">
          <div>
            <h2 className="font-heading text-xl font-black">New User</h2>
            <p className="font-mono text-[11px] text-zinc-500">Create login credentials + assign team hierarchy</p>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
        </div>
        <form id="newuser-form" onSubmit={submit} className="px-5 py-4 overflow-y-auto flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2">
            <div className="label-mono mb-1">EMAIL <span className="text-red-600">*</span></div>
            <input required type="email" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.email} onChange={(e) => upd("email", e.target.value.toLowerCase())} data-testid="newuser-email-input" />
          </label>
          <label className="block sm:col-span-2">
            <div className="label-mono mb-1 flex items-center justify-between">
              <span>PASSWORD <span className="text-red-600">*</span> <span className="text-zinc-400">(min 8 chars)</span></span>
              <button type="button" onClick={generatePassword} className="text-[10px] underline hover:text-blue-600" data-testid="newuser-generate-pw">Generate</button>
            </div>
            <div className="relative">
              <input required type={showPw ? "text" : "password"} className="brut-input w-full px-3 py-2 pr-10 text-sm font-mono" value={form.password} onChange={(e) => upd("password", e.target.value)} data-testid="newuser-password-input" minLength={8} />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>
          <label className="block sm:col-span-2">
            <div className="label-mono mb-1">FULL NAME</div>
            <input className="brut-input w-full px-3 py-2 text-sm" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Defaults to email prefix" />
          </label>
          <label className="block">
            <div className="label-mono mb-1">TEAM ROLE</div>
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.team_role} onChange={(e) => upd("team_role", e.target.value)} data-testid="newuser-team-role">
              <option value="employee">Employee</option>
              <option value="lead">Team Lead</option>
              <option value="manager">Team Manager</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <label className="block">
            <div className="label-mono mb-1">PRIMARY ROLE</div>
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.role} onChange={(e) => upd("role", e.target.value)}>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="label-mono mb-1">REPORTS TO (Manager)</div>
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.manager_id} onChange={(e) => upd("manager_id", e.target.value)} data-testid="newuser-manager">
              <option value="">— None —</option>
              {(allUsers || []).map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.team_role || "employee"})</option>)}
            </select>
          </label>
          <label className="block">
            <div className="label-mono mb-1">TEAM</div>
            <input className="brut-input w-full px-3 py-2 text-sm" value={form.team} onChange={(e) => upd("team", e.target.value)} placeholder="Drafting-A" />
          </label>
          <label className="block">
            <div className="label-mono mb-1">EMPLOYEE CODE</div>
            <input className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.employee_id} onChange={(e) => upd("employee_id", e.target.value)} />
          </label>
          <label className="block">
            <div className="label-mono mb-1">MONTHLY SALARY (₹)</div>
            <input type="number" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.monthly_salary_inr} onChange={(e) => upd("monthly_salary_inr", parseFloat(e.target.value) || 0)} />
          </label>
          <label className="block">
            <div className="label-mono mb-1">DESIGNATION (HR)</div>
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.designation} onChange={(e) => upd("designation", e.target.value)} data-testid="newuser-designation">
              <option value="">— Unset —</option>
              {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="label-mono mb-1">DEPARTMENT (HR)</div>
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.department} onChange={(e) => upd("department", e.target.value)} data-testid="newuser-department">
              <option value="">— Unset —</option>
              {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        </form>
        <div className="flex justify-between items-center gap-2 px-5 py-3 border-t border-zinc-900 bg-white shrink-0">
          <p className="font-mono text-[10px] text-zinc-500">User can sign in immediately with these credentials.</p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
            <button type="submit" form="newuser-form" disabled={saving} className="brut-btn px-4 py-2 text-sm disabled:opacity-50" data-testid="newuser-create-btn">{saving ? "CREATING..." : "CREATE USER"}</button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
